import { useState, useEffect, useRef, useCallback } from 'react';
import type { DocumentAST } from '../types';
import { editorApi as webbuilderApi } from '../api';

export type SyncStatus = 'idle' | 'saving' | 'saved' | 'publishing' | 'published' | 'error';

interface UsePageDraftSyncOptions {
  pageId: string;
  doc: DocumentAST;
  debounceMs?: number;
}

interface UsePageDraftSyncReturn {
  status: SyncStatus;
  lastSavedAt: Date | null;
  isDirty: boolean;
  saveNow: () => Promise<void>;
  publishPage: () => Promise<{ content_hash: string; version_no: number } | null>;
}

// usePageDraftSync performs debounced auto-saving and atomic compilation publishing (WB-I4).
export function usePageDraftSync({
  pageId,
  doc,
  debounceMs = 1500,
}: UsePageDraftSyncOptions): UsePageDraftSyncReturn {
  const [status, setStatus] = useState<SyncStatus>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isFirstMount = useRef(true);
  const lastSavedDocStr = useRef<string>('');

  const saveDraftToServer = useCallback(
    async (currentDoc: DocumentAST) => {
      if (!pageId || pageId === 'sample-page') {
        return;
      }
      const serialized = JSON.stringify(currentDoc);
      if (serialized === lastSavedDocStr.current) {
        return;
      }

      setStatus('saving');
      try {
        await webbuilderApi.saveDraft(pageId, {
          draft_ast: {
            ...currentDoc,
            page_id: pageId,
          },
          seo: currentDoc.seo || {},
        });
        lastSavedDocStr.current = serialized;
        setStatus('saved');
        setLastSavedAt(new Date());
      } catch {
        setStatus('error');
      }
    },
    [pageId]
  );

  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      lastSavedDocStr.current = JSON.stringify(doc);
      return;
    }
    if (!pageId || pageId === 'sample-page') {
      return;
    }

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      saveDraftToServer(doc);
    }, debounceMs);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [doc, pageId, debounceMs, saveDraftToServer]);

  // Periodic 5-minute background auto-save (Requirement 7)
  useEffect(() => {
    if (!pageId || pageId === 'sample-page') {
      return;
    }
    const interval = setInterval(() => {
      saveDraftToServer(doc);
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [doc, pageId, saveDraftToServer]);

  // Warn on uncommitted changes before closing tab
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (status === 'saving') {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [status]);

  const saveNow = useCallback(async () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    await saveDraftToServer(doc);
  }, [doc, saveDraftToServer]);

  const publishPage = useCallback(async () => {
    if (!pageId || pageId === 'sample-page') {
      return null;
    }
    setStatus('publishing');
    try {
      await saveDraftToServer(doc);
      const data = await webbuilderApi.publishPage(pageId);
      setStatus('published');
      return data;
    } catch {
      setStatus('error');
      return null;
    }
  }, [pageId, doc, saveDraftToServer]);

  const isDirty = JSON.stringify(doc) !== lastSavedDocStr.current;

  return {
    status,
    lastSavedAt,
    isDirty,
    saveNow,
    publishPage,
  };
}
